import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { handleUserMatchCheckin } from "../../userDashboard/userControllers/gameController";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import RegisterConfirmed from "../components/matchVerifyComps/RegisterConfirmed";
import RegisterError from "../components/matchVerifyComps/RegisterError";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

import load from "./assets/load.gif";

interface Booking {
    id: string;
    title: string;
    date: string;
    start_time?: string;
    pitch?: {
        name: string;
    };
}

const AuthMatchOptions: React.FC = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [registerConfirmed, setRegisterConfirmed] = useState(false);
    const [registerError, setRegisterError] = useState(false);

    const bookings = (state?.bookings || []) as Booking[];
    const email = (state?.email || "") as string;

    const handleCheckin = async (gameId: string) => {
        setLoading(true);
        try {
            const userData = { email, type: "checkin", model_type: "Game" };
            await handleUserMatchCheckin(
                gameId,
                userData,
                () => {
                    setLoading(false);
                    setRegisterConfirmed(true);
                },
                () => {
                    setLoading(false);
                    setRegisterError(true);
                }
            );
        } catch (error) {
            setLoading(false);
            setRegisterError(true);
        }
    };

    if (!email) {
        return (
            <div className="h-screen flex items-center justify-center font-Manrope bg-gray-50">
                <div className="text-center p-12 bg-white rounded-[40px] shadow-2xl border border-gray-100 max-w-md mx-4">
                    <div className="text-6xl mb-8">⚠️</div>
                    <h2 className="text-3xl font-black text-gray-900 mb-4">Session Expired</h2>
                    <p className="text-gray-500 font-bold mb-10">Please start the authentication process again.</p>
                    <button onClick={() => navigate("/authenticate-match")} className="w-full bg-green-700 text-white py-5 rounded-2xl font-black text-xl">Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Navbar />
            {registerConfirmed && <RegisterConfirmed setRegisterConfirmed={setRegisterConfirmed} />}
            {registerError && <RegisterError setRegisterError={setRegisterError} />}

            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 flex flex-col items-center justify-center p-4 text-center"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight">Select Your Match</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
                        Found {bookings.length} registration(s) for <span className="text-green-400 font-black">{email}</span>. Click your match to check in.
                    </p>
                </div>
            </div>

            <div className="flex flex-col items-center py-24 w-full px-4 md:px-14 bg-gray-50 min-h-[600px]">
                <div className="max-w-4xl w-full mx-auto">
                    {bookings.length === 0 ? (
                        <div className="bg-white p-20 rounded-[48px] text-center shadow-xl border border-dashed border-gray-200">
                            <h3 className="text-2xl font-black text-gray-400 mb-8 italic">No active bookings found.</h3>
                            <button onClick={() => navigate("/matches")} className="px-10 py-4 bg-green-700 text-white rounded-2xl font-black text-lg">Join a Match</button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {bookings.map((booking: Booking, i: number) => (
                                <div key={i} className="bg-white p-10 rounded-[40px] shadow-lg border border-gray-100 font-Manrope group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
                                    <div className="flex justify-between items-center mb-8">
                                        <span className="px-5 py-2 bg-green-100 text-green-700 rounded-full font-black text-xs uppercase tracking-widest">Active</span>
                                        <p className="font-bold text-gray-400">{new Date(booking.date).toLocaleDateString()}</p>
                                    </div>
                                    <h3 className="text-3xl font-black text-gray-900 mb-6 group-hover:text-green-700 transition-colors uppercase tracking-tighter">{booking.title}</h3>
                                    <div className="space-y-3 mb-12">
                                        <p className="text-gray-500 font-bold flex items-center gap-2 italic text-lg">🏟️ {booking.pitch?.name || "Main Stadium"}</p>
                                        <p className="text-gray-400 font-medium">🕒 {booking.start_time || "Scheduled"}</p>
                                    </div>
                                    <button
                                        onClick={() => handleCheckin(booking.id)}
                                        disabled={loading}
                                        className="w-full bg-gray-900 group-hover:bg-green-700 text-white py-5 rounded-2xl font-black text-xl transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3"
                                    >
                                        {loading ? <img src={load} className="w-6 h-6" alt="Loading" /> : "Verify Identity"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default AuthMatchOptions;
